import { Transition } from '@headlessui/react';
import React, { useCallback, useRef, useState } from 'react';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import PlayIcon from '@videoblocks/react-icons/Play';
import { LegacyVideo } from '@videoblocks/storywind';

import Logger from '../../common/Logger';
import { StockItem } from '../../common/types/StockItemTypes';
import { sendBeacon } from '../../common/utils';

import './SnapshotVideoThumbnail.less';

type SnapshotVideoThumbnailProps = {
  snapshotVideo: StockItem;
};

const SnapshotVideoThumbnail = ({
  snapshotVideo,
}: SnapshotVideoThumbnailProps) => {
  if (!snapshotVideo) return null;

  // This ref's current value is the component instance, not the actual DOM element.
  const videoComponentRef = useRef<LegacyVideo>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const playVideo = useCallback(() => {
    void videoComponentRef.current?.videoRef?.current?.play();
    setIsPlaying(true);
  }, [videoComponentRef]);

  const pauseVideo = useCallback(() => {
    void videoComponentRef.current?.videoRef?.current?.pause();
    setIsPlaying(false);
  }, [videoComponentRef]);

  const handleSnapshotsVideoThumbnailClick = () => {
    const stockItemId = snapshotVideo.id;
    const urlPath = '/api/video/stockItemClickEvent/details';
    const clickOriginArea = OriginArea.SNAPSHOTS_VIDEO_THUMBNAIL;

    const beacon = sendBeacon(urlPath, stockItemId, null, clickOriginArea);

    if (!beacon) {
      Logger.produceDetailsClickEvent(snapshotVideo, null, clickOriginArea);
    }
  };

  return (
    <section className="text-white text-sm flex flex-col items-center">
      <p className="w-full mb-3">This image was captured from a video:</p>
      <a
        className="relative max-w-xs"
        href={snapshotVideo.detailsUrl}
        onMouseEnter={playVideo}
        onFocus={playVideo}
        onMouseLeave={pauseVideo}
        onBlur={pauseVideo}
        onClick={handleSnapshotsVideoThumbnailClick}
        tabIndex={0}
      >
        <LegacyVideo
          videoUrl={snapshotVideo.previewUrl}
          poster={snapshotVideo.thumbnailUrl}
          preload={false}
          className="snapshotVideoThumbnail rounded-2xl object-fill h-full"
          ref={videoComponentRef}
        />

        {snapshotVideo.shouldStockItemHaveNewFlag && (
          <span className="absolute bg-white px-1.5 py-0.5 rounded top-4 left-4 leading-5 font-semibold text-gray-1000">
            New
          </span>
        )}

        <Transition
          show={!isPlaying}
          enter="ease duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute flex bg-gray-1000 w-15 h-15 inset-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full">
            <PlayIcon className="fill-white h-6 w-6 m-auto" />
          </div>
        </Transition>
      </a>
    </section>
  );
};

export default SnapshotVideoThumbnail;
